import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

export function ApprovalsPage() {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState([])
  const [commissionPeriods, setCommissionPeriods] = useState([])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [leaveRes, periodsRes] = await Promise.all([
        api.get('/hr/leave-requests', { params: { status: 'pending' } }),
        api.get('/masters/commission-periods').catch(() => ({ data: { data: { periods: [] } } })),
      ])
      setPending(leaveRes.data?.data?.leave_requests ?? [])
      setCommissionPeriods(
        periodsRes.data?.data?.periods ?? periodsRes.data?.data?.commission_periods ?? [],
      )
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load approvals inbox.'))
      setPending([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleReview(id, action) {
    setBusy(true)
    setError('')
    try {
      await api.post(`/hr/leave-requests/${id}/${action}`)
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to ${action} request.`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SalonPageShell
      title="Approvals"
      description="Pending leave requests and notes for POS / commission approvals."
    >
      {error ? (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <section className="mb-6 max-w-3xl space-y-3 rounded-2xl border border-border/70 bg-card p-4 text-sm text-muted-foreground">
        <p>
          Refunds and large discounts are approved at checkout (POS) when a manager override is
          required. Use Sales → Refunds for completed tickets.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/pos">Open checkout</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/sales">Open sales</Link>
          </Button>
        </div>
      </section>

      <section className="mb-6 max-w-3xl">
        <h2 className="mb-3 text-lg font-medium">Pending leave</h2>
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : (
          <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
            {pending.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">No pending leave requests.</li>
            ) : (
              pending.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="font-medium">{row.stylist?.name ?? `Staff #${row.stylist_id}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.starts_on} → {row.ends_on}
                      {row.reason ? ` · ${row.reason}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={busy} onClick={() => handleReview(row.id, 'approve')}>
                      Approve
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => handleReview(row.id, 'reject')}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </section>

      <section className="max-w-3xl">
        <h2 className="mb-3 text-lg font-medium">Commission periods</h2>
        <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
          {commissionPeriods.length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">
              No commission periods returned. Lock/approve from Staff commission tools when available.
            </li>
          ) : (
            commissionPeriods.slice(0, 20).map((period) => (
              <li key={period.id ?? `${period.period_start}-${period.period_end}`} className="px-4 py-3 text-sm">
                <p className="font-medium">
                  {period.period_start ?? period.starts_on ?? 'Period'} →{' '}
                  {period.period_end ?? period.ends_on ?? '—'}
                </p>
                <p className="text-xs capitalize text-muted-foreground">{period.status ?? 'open'}</p>
              </li>
            ))
          )}
        </ul>
      </section>
    </SalonPageShell>
  )
}
